import { z } from 'zod';

const viewId = z.string().min(1, 'viewId is required');
const fieldId = z.string().min(1, 'fieldId is required');

export const removeViewSchema = z.object({
  viewId
});

export const setDefaultViewSchema = z.object({
  viewId
});

export const setLayoutTypeSchema = z.object({
  viewId,
  layoutType: z.string().min(1, 'layoutType is required')
});

export const setCardWidthSchema = z.object({
  viewId,
  cardWidth: z.string().min(1, 'cardWidth is required')
});

export const setNumberedEntriesSchema = z.object({
  viewId,
  enabled: z.boolean()
});

export const setShowFieldNamesSchema = z.object({
  viewId,
  enabled: z.boolean()
});

export const setGroupFieldSchema = z.object({
  viewId,
  fieldId
});

export const addSortSchema = z.object({
  viewId,
  fieldId,
  ascending: z.boolean()
});

export const updateSortSchema = z.object({
  viewId,
  fieldId,
  ascending: z.boolean()
});

export const removeSortSchema = z.object({
  viewId,
  fieldId
});

export const setFilterSchema = z.object({
  viewId,
  filter: z.object({}).passthrough()
});

export const clearFilterSchema = z.object({
  viewId
});

export const updateNameSchema = z.object({
  viewId,
  newName: z.string().min(1, 'View name cannot be empty')
});

export const applyViewNameDeltaSchema = z.object({
  viewId,
  delta: z.array(z.record(z.unknown())).min(1, 'delta is required')
});

export const hideFieldsSchema = z.object({
  viewId,
  fieldIds: z.array(fieldId).min(1, 'At least one fieldId is required')
});

export const showFieldsSchema = z.object({
  viewId,
  fieldIds: z.array(fieldId).min(1, 'At least one fieldId is required')
});

export const reorderViewSchema = z.object({
  viewId,
  newIndex: z
    .number()
    .int('newIndex must be an integer')
    .nonnegative('newIndex cannot be negative')
});

export const updateLayoutSchema = z.object({
  viewId,
  layoutType: z.string().min(1).optional(),
  groupFieldId: z.string().min(1).optional(),
  numberedEntries: z.boolean().optional(),
  cardWidth: z.string().min(1).optional(),
  showFieldNames: z.boolean().optional()
});

/**
 * Validates input for a full replacement of a view's properties.
 * `properties` is optional — omitting or passing `undefined` clears
 * the properties bag.
 */
export const setPropertiesSchema = z.object({
  viewId,
  properties: z.record(z.unknown()).optional()
});

/**
 * Validates input for a partial patch of a view's properties.
 * `set` upserts keys; `unset` removes them.
 */
export const updatePropertiesSchema = z.object({
  viewId,
  set: z.record(z.unknown()).optional(),
  unset: z.array(z.string()).optional()
});

export type RemoveViewInput = z.infer<typeof removeViewSchema>;
export type SetDefaultViewInput = z.infer<typeof setDefaultViewSchema>;
export type SetLayoutTypeInput = z.infer<typeof setLayoutTypeSchema>;
export type SetCardWidthInput = z.infer<typeof setCardWidthSchema>;
export type SetNumberedEntriesInput = z.infer<typeof setNumberedEntriesSchema>;
export type SetShowFieldNamesInput = z.infer<typeof setShowFieldNamesSchema>;
export type SetGroupFieldInput = z.infer<typeof setGroupFieldSchema>;
export type AddSortInput = z.infer<typeof addSortSchema>;
export type UpdateSortInput = z.infer<typeof updateSortSchema>;
export type RemoveSortInput = z.infer<typeof removeSortSchema>;
export type SetFilterInput = z.infer<typeof setFilterSchema>;
export type ClearFilterInput = z.infer<typeof clearFilterSchema>;
export type UpdateNameInput = z.infer<typeof updateNameSchema>;
export type HideFieldsInput = z.infer<typeof hideFieldsSchema>;
export type ShowFieldsInput = z.infer<typeof showFieldsSchema>;
export type ReorderViewInput = z.infer<typeof reorderViewSchema>;
export type UpdateLayoutInput = z.infer<typeof updateLayoutSchema>;
export type SetPropertiesInput = z.infer<typeof setPropertiesSchema>;
export type UpdatePropertiesInput = z.infer<typeof updatePropertiesSchema>;
export type ApplyViewNameDeltaInput = z.infer<typeof applyViewNameDeltaSchema>;
